/* ============================================
   SeleccionarCuenta — Pantalla "Selecciona una cuenta"
   (estilo Cloudflare). Aparece tras el login cuando la
   identidad tiene más de una cuenta (membresía). Al
   elegir, re-emite el JWT y entra al panel del rol.
   ============================================ */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth, type Account, type UserRole } from '@/hooks/useAuth'
import { crossDomainTarget } from '@/lib/domain'
import { useTheme } from '@/hooks/useTheme'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

function panelForRole(role: UserRole): string {
  if (role === 'admin') return '/'
  if (role === 'partner') return '/partner/pipeline'
  return '/portal'
}

const roleLabel: Record<string, string> = { cliente: 'Cliente', partner: 'Partner', admin: 'Admin' }

export default function SeleccionarCuenta() {
  const [tema] = useTheme()
  const { isAuthenticated, loading, accounts, needsAccountSelection, role, switchAccount, signOut, user } = useAuth()
  const navigate = useNavigate()
  const [localAccounts, setLocalAccounts] = useState<Account[]>([])
  const [switchingId, setSwitchingId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Separación por dominio: una identidad pendiente (no-admin) no debe quedar en el dominio admin
  useEffect(() => {
    const t = crossDomainTarget(null, user?.email)
    if (t) window.location.replace(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email])

  // Si ya hay cuenta activa (no pendiente), no debería estar aquí → al panel
  useEffect(() => {
    if (!loading && isAuthenticated && !needsAccountSelection && role) {
      navigate(panelForRole(role), { replace: true })
    }
    if (!loading && !isAuthenticated) {
      navigate('/login', { replace: true })
    }
  }, [loading, isAuthenticated, needsAccountSelection, role])

  // Si la lista viene vacía (p.ej. tras refrescar con token de identidad), la traemos
  useEffect(() => {
    if (accounts.length > 0) { setLocalAccounts(accounts); return }
    const token = localStorage.getItem('auth_token')
    if (!token) return
    fetch(`${API_URL}/api/auth/accounts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { accounts: [] })
      .then(d => setLocalAccounts(Array.isArray(d.accounts) ? d.accounts : []))
      .catch(() => {})
  }, [accounts])

  const list = accounts.length > 0 ? accounts : localAccounts

  async function choose(acc: Account) {
    setSwitchingId(acc.cliente_id)
    setError(null)
    try {
      const newRole = await switchAccount(acc.cliente_id)
      navigate(panelForRole(newRole || acc.role), { replace: true })
    } catch (e: any) {
      setError(e?.message || 'No se pudo entrar a la cuenta')
      setSwitchingId(null)
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg-body, #0F1117)', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 520 }}>
        <div style={{ marginBottom: 20, textAlign: 'center' }}>
          <img src={`/assets/brand/logo-full-${tema === 'dark' ? 'white' : 'color'}.svg`} alt="1Conecta" style={{ height: 34, marginBottom: 18 }} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }} />
          <h1 style={{ fontSize: '1.5rem', margin: '0 0 6px', color: 'var(--text-primary, #fff)' }}>Selecciona una cuenta</h1>
          <p style={{ color: 'var(--text-secondary, #9ca3af)', fontSize: '0.9rem', margin: 0 }}>
            {user?.email ? <>Tu correo <strong>{user.email}</strong> tiene acceso a varias cuentas.</> : 'Tu correo tiene acceso a varias cuentas.'}
          </p>
        </div>

        {error && (
          <div style={{ background: 'color-mix(in srgb, var(--danger) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--danger) 35%, transparent)', color: 'var(--danger)', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: '0.85rem' }}>{error}</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--text-muted, #9ca3af)', padding: 24 }}>
              <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />
              Cargando cuentas...
            </div>
          )}
          {list.map(acc => (
            <button
              key={acc.cliente_id}
              type="button"
              disabled={switchingId !== null}
              onClick={() => choose(acc)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                padding: '14px 16px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                background: 'var(--bg-card, #151a23)', border: '1px solid var(--border, #232a36)',
                color: 'var(--text-primary, #fff)', transition: 'border-color 0.15s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent-primary, #00d2d3)' }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border, #232a36)' }}
            >
              <span style={{ width: 40, height: 40, borderRadius: 8, flexShrink: 0, display: 'grid', placeItems: 'center', background: 'var(--accent-primary, #4F7FE8)', color: 'var(--text-on-primary)', fontSize: '1.1rem', fontWeight: 700 }}>
                {(acc.empresa || 'E').charAt(0).toUpperCase()}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acc.empresa || `Empresa #${acc.cliente_id}`}</span>
                <span style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary, #9ca3af)' }}>Entrar como {roleLabel[acc.role] || acc.role}</span>
              </span>
              {switchingId === acc.cliente_id
                ? <div className="auth-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>}
            </button>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <button type="button" onClick={() => { signOut(); navigate('/login', { replace: true }) }} style={{ background: 'none', border: 'none', color: 'var(--text-muted, #9ca3af)', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}>
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  )
}
